import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, FileText } from 'lucide-react';
import * as React from 'react';
import type { Application, Hit, HitDetail, staffApi, VendorHitRecord } from '@ps/api-client/staff';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Card, CardTitle, Details } from '@ps/ui-kit/components/card';
import { Pill } from '@ps/ui-kit/components/pill';
import { SearchBar } from '@ps/ui-kit/components/search-bar';
import { Loading } from '@ps/ui-kit/components/states';
import { Table } from '@ps/ui-kit/components/table';
import { Tabs } from '@ps/ui-kit/components/tabs';
import { cn } from '@ps/ui-kit/lib/cn';
import { countryName, date, dateTime, titleCase } from '@ps/ui-kit/lib/format';
import { listTone, listTypeLabels } from './labels';

// Screening evidence on the Screening tab (ADR-084): the officer compares what we hold with everything the provider
// sent about a possible match, sees all of the provider's record (sources with links and listing dates, associates,
// employment, adverse media, notes), and opens the provider's own report. Read-only; every look is audited server-side.

type Api = ReturnType<typeof staffApi>;

// ─────────── reading the provider's record (Shufti's shape; anything unknown still shows under "Full record") ───────────

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {});
const strings = (v: unknown): string[] => arr(v).map(x => str(x) ?? str(obj(x).value) ?? str(obj(x).name)).filter((x): x is string => !!x);
const words = (s: string) => s.replace(/[_-]+/g, ' ').replace(/^\w/, c => c.toUpperCase());
const uniq = (xs: string[]) => [...new Set(xs.map(x => x.trim()).filter(Boolean))];

export interface VendorSource { name: string; publisher: string | null; description: string | null; url: string | null; categories: string[]; listedAt: string | null; updatedAt: string | null }
export interface VendorMedia { title: string | null; url: string | null; date: string | null; source: string | null; snippet: string | null; other: Array<[string, string]> }

/** The provider record as plain parts. Pure, so it is unit-tested against a real-shaped record. */
export function readVendorRecord(r: VendorHitRecord) {
  const fields = Object.entries(obj(r.fields)).map(([label, vs]) => ({ label, tag: str(obj(arr(vs)[0]).tag), values: uniq(arr(vs).map(v => str(obj(v).value) ?? '')) }))
    .filter(f => f.values.length);
  const byTag = (...tags: string[]) => uniq(fields.filter(f => f.tag && tags.includes(f.tag)).flatMap(f => f.values));
  const details = arr(r.source_details).map(obj);
  const sources: VendorSource[] = details.length
    ? details.map(s => ({ name: str(s.publisher) ?? str(s.description) ?? 'Source', publisher: str(s.publisher), description: str(s.description), url: str(s.url) ?? str(obj(s.refrence_url).url),
        categories: uniq([...strings(s.categories), ...arr(s.source_categories).flatMap(c => Object.keys(obj(c)))]), listedAt: str(s.created_at), updatedAt: str(s.updated_at) }))
    : Object.values(obj(r.source_notes)).map(obj).map(s => ({ name: str(s.name) ?? 'Source', publisher: null, description: str(s.description), url: str(s.url),
        categories: strings(s.aml_types), listedAt: str(s.listing_started_utc), updatedAt: str(s.listing_ended_utc) }));
  const pepLevels = uniq(details.flatMap(s => arr(s.source_categories).flatMap(c => Object.keys(obj(c)))).filter(k => /^PEP/i.test(k)));
  const associates = [
    ...arr(r.associates).map(obj).map(a => ({ name: str(a.name) ?? '—', relation: str(a.association) ?? str(a.relation) })),
    ...Object.entries(obj(r.associates)).filter(([k]) => !/^\d+$/.test(k)).flatMap(([k, v]) => arr(v).map(x => ({ name: str(x) ?? str(obj(x).name) ?? '—', relation: words(k) }))),
    ...details.flatMap(s => arr(obj(s.data).linked_entities).map(obj).map(l => ({ name: strings(l.name).join(', ') || '—', relation: strings(l.relation).join(', ') || null }))),
  ];
  const employment = details.flatMap(s => arr(obj(s.data).employment_records).map(obj).map(e => ({
    firm: strings(e.firm_name).join(', ') || null, role: strings(e.role).join(', ') || null, address: strings(e.firm_address).join(', ') || null })));
  const media: VendorMedia[] = arr(r.media).map(m => {
    const o = obj(m);
    const known = ['title', 'url', 'link', 'date', 'published_at', 'source', 'publisher', 'snippet', 'summary', 'description'];
    return { title: str(o.title), url: str(o.url) ?? str(o.link), date: str(o.date) ?? str(o.published_at), source: str(o.source) ?? str(o.publisher),
      snippet: str(o.snippet) ?? str(o.summary) ?? str(o.description),
      other: Object.entries(o).filter(([k]) => !known.includes(k)).map(([k, v]) => [words(k), typeof v === 'object' ? JSON.stringify(v) : String(v)] as [string, string]) };
  });
  const notes = uniq(details.flatMap(s => {
    const add = obj(obj(s.data).additional_information);
    return [...strings(add.notes_remarks), ...strings(add.comment), ...strings(add.identification_remarks), ...(str(obj(s.data).legal_notice) ? [str(obj(s.data).legal_notice)!] : [])];
  }));
  return {
    name: str(r.name), entityType: str(r.entity_type), aliases: uniq(strings(r.alternative_names)), matchTypes: uniq(strings(r.match_types)).map(words),
    types: uniq(strings(r.types)), fields, sources, pepLevels, associates, employment, media, notes,
    dob: byTag('date_of_birth', 'year_of_birth'), nationality: byTag('nationality', 'citizenship'), gender: byTag('gender'), address: byTag('address'),
    job: byTag('designation', 'role', 'position', 'occupation', 'profession', 'title'), employer: uniq([...byTag('company_name', 'employer'), ...employment.map(e => e.firm ?? '')]),
    email: byTag('email'), phone: byTag('phone'), dateOfDeath: byTag('date_of_death'), countries: byTag('country', 'countries'),
  };
}
type VendorView = ReturnType<typeof readVendorRecord>;

// ─────────── the comparison ───────────

type Tone = 'match' | 'differs' | 'similar' | 'info';
const norm = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
function sameCountry(a: string, b: string) {
  const n = (v: string) => (countryName(v) ?? v).toLowerCase().replace(/[^a-z ]/g, '').trim();
  const x = n(a), y = n(b);
  return x === y || x.includes(y) || y.includes(x);
}
function Mark({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return <span className={cn('rounded px-1 box-decoration-clone', tone === 'match' ? 'bg-ok-bg' : tone === 'differs' ? 'bg-warn-bg' : '')}>{children}</span>;
}

/** What this person is to the applicant, in words: "Director · Owner 55%", or the applicant itself. */
function roleOf(app: Application, partyId: string): string | null {
  const p = app.parties.find(x => x.id === partyId);
  if (!p) return null;
  if (p.isApplicant) return app.type === 'CORPORATE' ? 'The applicant company' : `Applicant${app.industry ? ` · ${app.industry}` : ''}`;
  const rel = app.relationships.filter(r => r.fromPartyId === partyId);
  const label: Record<string, string> = { DIRECTOR_OF: 'Director', OWNS: 'Owner', SIGNATORY_OF: 'Authorised signatory', SHAREHOLDER_OF: 'Shareholder' };
  return uniq(rel.map(r => `${label[r.type] ?? words(r.type)}${r.ownershipPct ? ` ${r.ownershipPct}%` : ''}`)).join(' · ') || null;
}

export function MatchComparison({ app, hit, detail }: { app: Application; hit: Hit; detail: HitDetail | null }) {
  const p = app.parties.find(x => x.id === hit.partyId);
  const v = detail?.detail ? readVendorRecord(detail.detail) : null;
  const la = hit.listedAttributes as Record<string, string | undefined>;
  const listedDob = v?.dob[0] ?? la.dob ?? null;
  const listedNat = v?.nationality.join(', ') || la.nationality || null;
  const dobGap = p?.dob && listedDob && /^\d{4}/.test(listedDob) ? Math.abs(new Date(p.dob).getFullYear() - Number(listedDob.slice(0, 4))) : null;
  const ourKind = p?.kind === 'ENTITY' ? 'Company' : 'Person';
  const theirKind = v?.entityType ? (/person|individual/i.test(v.entityType) ? 'Person' : 'Company') : null;
  const join = (xs: string[] | undefined) => (xs && xs.length ? xs.join(' · ') : null);
  const company = app.type === 'CORPORATE' && !p?.isApplicant ? app.legalName : null;
  const rows: Array<{ label: string; ours: React.ReactNode; theirs: React.ReactNode; tone: Tone; note?: string; sub?: string }> = [
    { label: 'Name', ours: p?.name, theirs: hit.listedName, tone: p && norm(p.name) === norm(hit.listedName) ? 'match' : 'similar',
      sub: v?.aliases.length ? `Also known as ${v.aliases.slice(0, 6).join(' · ')}${v.aliases.length > 6 ? ` +${v.aliases.length - 6}` : ''}` : undefined },
    ...(v?.matchTypes.length ? [{ label: 'Matched on', ours: null, theirs: v.matchTypes.join(' · '), tone: 'info' as Tone }] : []),
    ...(theirKind ? [{ label: 'Person or company', ours: ourKind, theirs: theirKind, tone: (theirKind === ourKind ? 'match' : 'differs') as Tone }] : []),
    { label: p?.kind === 'ENTITY' ? 'Incorporated' : 'Date of birth', ours: date(p?.dob ?? p?.incorporationDate ?? null), theirs: listedDob ? (listedDob.length > 4 ? date(listedDob) : listedDob) : null,
      tone: !p?.dob || !listedDob ? 'info' : listedDob.length > 4 ? (p.dob === listedDob ? 'match' : 'differs') : (dobGap === 0 ? 'match' : 'differs'),
      note: dobGap ? `differs by ${dobGap} years` : undefined },
    { label: 'Nationality', ours: countryName(p?.nationality), theirs: listedNat ? listedNat.split(', ').map(x => countryName(x) ?? x).join(', ') : null,
      tone: !p?.nationality || !listedNat ? 'info' : listedNat.split(', ').some(x => sameCountry(p.nationality!, x)) ? 'match' : 'differs' },
    ...(v?.gender.length ? [{ label: 'Gender', ours: <span className="text-ink-3">not collected</span>, theirs: join(v.gender), tone: 'info' as Tone }] : []),
    ...(v?.address.length || detail?.ours.address ? [{ label: 'Address', ours: detail?.ours.address, theirs: join(v?.address), tone: 'info' as Tone }] : []),
    ...(v?.job.length ? [{ label: 'Role / job', ours: roleOf(app, hit.partyId), theirs: join(v.job), tone: 'info' as Tone }] : []),
    ...(v?.employer.length ? [{ label: 'Employer', ours: company, theirs: join(v.employer), tone: 'info' as Tone }] : []),
    ...(v?.email.length || v?.phone.length ? [{ label: 'Email / phone', ours: p?.isApplicant ? app.email : null, theirs: join([...(v?.email ?? []), ...(v?.phone ?? [])]), tone: 'info' as Tone }] : []),
    ...(detail?.ours.idDocNumberMasked ? [{ label: 'ID document', ours: <span className="font-mono">{detail.ours.idDocNumberMasked}</span>, theirs: null, tone: 'info' as Tone }] : []),
    { label: 'Lists', ours: null, theirs: uniq([la.programme ?? '', ...listTypeLabels(v?.types.length ? v.types : hit.types), ...(v?.pepLevels ?? [])]).join(' · ') || null, tone: 'info' },
    ...(v?.dateOfDeath.length || la.dateOfDeath ? [{ label: 'Date of death', ours: null, theirs: v?.dateOfDeath[0] ?? la.dateOfDeath, tone: 'differs' as Tone }] : []),
  ];
  const dash = <span className="text-ink-3">—</span>;
  return (
    <div>
      <h4 className="eyebrow mb-2">Customer record against the listed person</h4>
      <Table bare compact label="Comparison" className="rounded-lg border border-line" tableClassName="[overflow-wrap:anywhere]">
        <thead><tr><th className="w-36">Detail</th><th>Customer record</th><th>Listed person</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.label}>
              <th scope="row" className="font-normal text-ink-3">{r.label}</th>
              <td>{r.ours ? <Mark tone={r.tone}>{r.ours}</Mark> : dash}</td>
              <td>{r.theirs ? <Mark tone={r.tone}>{r.theirs}</Mark> : dash}{r.note && <span className="ml-2 text-xs font-semibold text-warn">{r.note}</span>}
                {r.sub && <span className="mt-0.5 block text-xs text-ink-3">{r.sub}</span>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-xs text-ink-3"><Mark tone="match">green</Mark> the same · <Mark tone="differs">amber</Mark> different · unmarked: compare by eye</p>
    </div>
  );
}

// ─────────── the match at a glance ───────────

const statusTone = (h: Hit) => (!h.disposition ? 'bad' : h.disposition === 'FALSE_POSITIVE' ? 'ok' : h.disposition === 'TRUE_MATCH' ? 'bad' : 'warn');

/** Who the provider listed, how strong the match is, which lists, and the key facts, before any detail. */
export function MatchSummary({ hit, detail }: { hit: Hit; detail: HitDetail | null }) {
  const v = detail?.detail ? readVendorRecord(detail.detail) : null;
  const la = hit.listedAttributes as Record<string, string | undefined>;
  const lists = uniq([...listTypeLabels(v?.types.length ? v.types : hit.types), ...(v?.pepLevels ?? [])]);
  const dob = v?.dob[0] ?? la.dob ?? null;
  const nationality = v?.nationality.length ? v.nationality : la.nationality ? [la.nationality] : [];
  const facts: Array<[string, string | null]> = [
    ['Date of birth', dob ? (dob.length > 4 ? date(dob) : dob) : null],
    ['Nationality', nationality.map(x => countryName(x) ?? x).join(', ') || null],
    ['Countries', v?.countries.map(x => countryName(x) ?? x).join(', ') || null],
    ['Person or company', v?.entityType ? words(v.entityType) : null],
  ];
  return (
    <Card className="p-4" aria-label="Match summary">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="eyebrow">Possible match for {hit.partyName}</p>
          <h3 className="mt-1 font-display text-xl font-bold text-ink [overflow-wrap:anywhere]">{hit.listedName}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Pill tone={statusTone(hit)}>{hit.disposition ? titleCase(hit.disposition) : 'Open'}</Pill>
            {lists.map(l => <Pill key={l} tone={listTone(l)}>{l}</Pill>)}
            {hit.vendorNoScore && <Pill tone="warn">Vendor returned no score — shown at threshold</Pill>}
          </div>
        </div>
        <div className="shrink-0 rounded-xl bg-canvas px-4 py-2 text-center">
          <p className="font-display text-3xl font-bold leading-none text-ink tabular">{hit.score}</p>
          <p className="mt-1 text-[11px] font-medium uppercase tracking-[.06em] text-ink-3">match score</p>
        </div>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-3 @2xl:grid-cols-4">
        {facts.map(([k, val]) => (
          <div key={k} className="min-w-0">
            <dt className="text-[11px] font-medium uppercase tracking-[.06em] text-ink-3">{k}</dt>
            <dd className="mt-0.5 text-sm text-ink [overflow-wrap:anywhere]">{val ?? <span className="text-ink-3">—</span>}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// ─────────── everything the provider sent ───────────

const Link = ({ url, children }: { url: string; children: React.ReactNode }) => (
  <a href={url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 font-semibold text-tick-ink hover:underline">{children}<ExternalLink className="size-3" aria-hidden /></a>
);
const safeUrl = (u: string | null) => (u && /^https?:\/\//i.test(u) ? u : null);
const Count = ({ n }: { n: number }) => <span className="ml-1 rounded-full bg-canvas px-1.5 py-px text-[11px] font-semibold tabular text-ink-3">{n}</span>;
const Label = ({ children }: { children: React.ReactNode }) => <span className="text-[11px] font-medium uppercase tracking-[.06em] text-ink-3">{children}</span>;
const Item = ({ children }: { children: React.ReactNode }) => <li className="rounded-lg border border-line p-3 text-sm">{children}</li>;
const None = ({ children }: { children: React.ReactNode }) => <p className="text-sm text-ink-3">{children}</p>;

/** Long provider text: two lines, then "Show more". */
function Clamp({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  const long = text.length > 180;
  return (
    <div className="mt-1.5">
      <p className={cn('text-ink-2 [overflow-wrap:anywhere]', long && !open && 'line-clamp-2')}>{text}</p>
      {long && <button type="button" className="mt-0.5 text-xs font-semibold text-tick-ink hover:underline" aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? 'Show less' : 'Show more'}</button>}
    </div>
  );
}

/** The first `limit` of a long list, then "Show all N"; everything shows while a search is narrowing it. */
function useLimit<T>(items: T[], limit: number, all = false) {
  const [open, setOpen] = React.useState(false);
  const shown = open || all ? items : items.slice(0, limit);
  const more = items.length > shown.length
    ? <button type="button" className="mt-3 text-sm font-semibold text-tick-ink hover:underline" onClick={() => setOpen(true)}>Show all {items.length}</button> : null;
  return [shown, more] as const;
}

function ProfileTab({ v }: { v: VendorView }) {
  return (
    <div className="space-y-4">
      {(v.aliases.length > 0 || v.matchTypes.length > 0) && (
        <div className="space-y-2 rounded-lg bg-canvas p-3 text-sm">
          {v.aliases.length > 0 && <div><Label>Also known as</Label><div className="mt-1 flex flex-wrap gap-1.5">{v.aliases.map(a => <span key={a} className="rounded-full border border-line bg-surface px-2 py-0.5 text-ink">{a}</span>)}</div></div>}
          {v.matchTypes.length > 0 && <div><Label>Matched on</Label><p className="mt-1 text-ink">{v.matchTypes.join(' · ')}</p></div>}
        </div>
      )}
      {v.fields.length ? (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 @lg:grid-cols-2">
          {v.fields.map(f => (
            <div key={f.label} className="min-w-0 border-b border-line pb-2">
              <dt><Label>{f.label}</Label></dt>
              <dd className="mt-0.5 text-sm text-ink [overflow-wrap:anywhere]">{safeUrl(f.values[0] ?? null) ? <Link url={f.values[0]!}>Open link</Link> : f.values.join(' · ')}</dd>
            </div>
          ))}
        </dl>
      ) : <None>No profile fields.</None>}
    </div>
  );
}

function SourcesTab({ v }: { v: VendorView }) {
  const [shown, more] = useLimit(v.sources, 10);
  return (
    <>
      <ul className="space-y-2">
        {shown.map((s, i) => (
          <Item key={i}>
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
              <p className="min-w-0 flex-1 font-semibold text-ink [overflow-wrap:anywhere]">{s.name}</p>
              {safeUrl(s.url) && <Link url={s.url!}>Open source</Link>}
            </div>
            {s.categories.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{s.categories.map(c => <Pill key={c} tone={listTone(c)}>{c}</Pill>)}</div>}
            {(s.listedAt || s.updatedAt) && <p className="mt-1.5 text-xs text-ink-3">{[s.listedAt && `Listed ${date(s.listedAt)}`, s.updatedAt && `Updated ${date(s.updatedAt)}`].filter(Boolean).join(' · ')}</p>}
            {s.description && s.description !== s.name && <Clamp text={s.description} />}
          </Item>
        ))}
      </ul>
      {more}
    </>
  );
}

function MediaTab({ v }: { v: VendorView }) {
  const [shown, more] = useLimit(v.media, 10);
  return (
    <>
      <ul className="space-y-2">
        {shown.map((m, i) => (
          <Item key={i}>
            <p className="font-semibold text-ink [overflow-wrap:anywhere]">{safeUrl(m.url) ? <Link url={m.url!}>{m.title ?? 'Article'}</Link> : (m.title ?? 'Article')}</p>
            {(m.source || m.date) && <p className="mt-0.5 text-xs text-ink-3">{[m.source, m.date ? date(m.date) : null].filter(Boolean).join(' · ')}</p>}
            {m.snippet && <Clamp text={m.snippet} />}
            {m.other.length > 0 && <div className="mt-2"><Details rows={m.other} /></div>}
          </Item>
        ))}
      </ul>
      {more}
    </>
  );
}

function AssociatesTab({ v }: { v: VendorView }) {
  const [term, setTerm] = React.useState('');
  const q = term.trim().toLowerCase();
  const found = q ? v.associates.filter(a => `${a.name} ${a.relation ?? ''}`.toLowerCase().includes(q)) : v.associates;
  const [shown, more] = useLimit(found, 30, !!q);
  const groups = [...shown.reduce((m, a) => m.set(a.relation ?? 'Associate', [...(m.get(a.relation ?? 'Associate') ?? []), a.name]), new Map<string, string[]>())];
  return (
    <div className="space-y-4">
      {v.associates.length > 12 && <SearchBar label="Search associates" placeholder={`Search ${v.associates.length} names`} value={term} onChange={setTerm} className="h-9 max-w-sm" />}
      {found.length === 0 ? <None>No associate fits this search.</None> : groups.map(([relation, names]) => (
        <section key={relation}>
          <h4 className="mb-2"><Label>{words(relation)} · {names.length}</Label></h4>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm text-ink @md:grid-cols-2 @3xl:grid-cols-3">
            {names.map((n, i) => <li key={`${n}-${i}`} className="truncate border-b border-line/60 pb-1.5" title={n}>{n}</li>)}
          </ul>
        </section>
      ))}
      {more}
    </div>
  );
}

function EmploymentTab({ v }: { v: VendorView }) {
  return (
    <ul className="grid grid-cols-1 gap-2 @lg:grid-cols-2">
      {v.employment.map((e, i) => (
        <Item key={i}>
          <p className="font-semibold text-ink">{e.role ?? 'Role not given'}</p>
          {e.firm && <p className="mt-0.5 text-ink-2">{e.firm}</p>}
          {e.address && <p className="mt-0.5 text-xs text-ink-3">{e.address}</p>}
        </Item>
      ))}
    </ul>
  );
}

export function VendorDetails({ detail, vendor }: { detail: HitDetail; vendor: string }) {
  const [tab, setTab] = React.useState('profile');
  const [raw, setRaw] = React.useState(false);
  if (!detail.detail) return null;
  const v: VendorView = readVendorRecord(detail.detail);
  const tabs = [
    { value: 'profile', title: 'Profile', n: null, content: <ProfileTab v={v} /> },          // one profile per match: a count here read as "6 profiles"
    { value: 'sources', title: 'Sources', n: v.sources.length, content: <SourcesTab v={v} /> },
    { value: 'media', title: 'Media', n: v.media.length, content: <MediaTab v={v} /> },
    { value: 'associates', title: 'Associates', n: v.associates.length, content: <AssociatesTab v={v} /> },
    { value: 'employment', title: 'Jobs', n: v.employment.length, content: <EmploymentTab v={v} /> },
    { value: 'notes', title: 'Notes', n: v.notes.length, content: (
      <ul className="space-y-2 text-sm text-ink-2">{v.notes.map(n => <li key={n} className="border-l-2 border-line pl-3 [overflow-wrap:anywhere]">{n}</li>)}</ul>
    ) },
  ];
  return (
    <Card className="@container p-4" aria-label={`All details from ${vendor}`}>
      <CardTitle eyebrow aside={<span className="text-xs text-ink-3">{detail.obtained === 'REFETCHED' ? `Fetched again ${dateTime(detail.obtainedAt)}` : 'Kept at screening'}</span>}>
        All details from {vendor}
      </CardTitle>
      <Tabs compact label="Provider record" value={tab} onValueChange={setTab} tabs={tabs.map(t => ({
        value: t.value, label: <>{t.title}{t.n !== null && <Count n={t.n} />}</>, content: t.content, disabled: t.n === 0,
      }))} />
      <div className="mt-4 border-t border-line pt-3">
        <button type="button" className="text-sm font-semibold text-tick-ink hover:underline" aria-expanded={raw} onClick={() => setRaw(r => !r)}>
          {raw ? 'Hide the full record' : 'Show the full record as the provider sent it'}
        </button>
        {raw && <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-canvas p-3 text-xs">{JSON.stringify(detail.detail, null, 2)}</pre>}
      </div>
    </Card>
  );
}

// ─────────── the provider's reports, and fetching details again ───────────

export function ScreeningReports({ api, applicationId, canRefresh }: { api: Api; applicationId: string; canRefresh: boolean }) {
  const qc = useQueryClient();
  const reports = useQuery({ queryKey: ['screening-reports', applicationId], queryFn: () => api.screeningReports(applicationId) });
  const [busy, setBusy] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  async function open(eventId: string) {
    setBusy(eventId); setNotice(null);
    try {
      const url = URL.createObjectURL(await api.screeningReportFile(applicationId, eventId));
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { setNotice({ tone: 'bad', text: "We couldn't open the report. Please try again." }); } finally { setBusy(null); }
  }
  async function refresh() {
    setBusy('refresh'); setNotice(null);
    try {
      const r = await api.refreshScreeningDetails(applicationId);
      await qc.invalidateQueries({ queryKey: ['hit-detail'] });
      await qc.invalidateQueries({ queryKey: ['screening-reports', applicationId] });
      setNotice({ tone: 'ok', text: r.details || r.reports
        ? `Fetched ${r.details} match detail${r.details === 1 ? '' : 's'}${r.reports ? `; ${r.reports} report${r.reports === 1 ? ' is' : 's are'} being downloaded` : ''}.`
        : 'Everything the provider holds for these screenings is already here.' });
    } catch (e) { setNotice({ tone: 'bad', text: e instanceof Error ? e.message : 'Something went wrong. Please try again.' }); } finally { setBusy(null); }
  }

  const items = reports.data?.items ?? [];
  return (
    <Card className="p-4" aria-label="Provider reports">
      <CardTitle eyebrow aside={canRefresh && <Button size="sm" variant="secondary" disabled={busy !== null} onClick={() => void refresh()}>{busy === 'refresh' ? 'Fetching…' : 'Get full details from the provider'}</Button>}>
        Provider reports
      </CardTitle>
      {notice && <Alert tone={notice.tone} className="mb-3">{notice.text}</Alert>}
      {reports.isPending ? <Loading /> : items.length === 0 ? <p className="text-sm text-ink-3">No report from the screening provider yet.</p> : (
        <ul className="divide-y divide-line text-sm">
          {items.map(r => (
            <li key={r.screeningEventId} className="flex flex-wrap items-center gap-3 py-2">
              <FileText className="size-4 text-ink-3" aria-hidden />
              <span className="font-semibold text-ink">{r.subjectName}</span>
              <span className="text-ink-3">{dateTime(r.storedAt)} · {Math.max(1, Math.round(r.sizeBytes / 1024))} KB</span>
              <Button size="sm" variant="ghost" className="ml-auto" disabled={busy !== null} onClick={() => void open(r.screeningEventId)}>{busy === r.screeningEventId ? 'Opening…' : 'Open report'}</Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
